
import { useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d6blvjrv53dtlm7j7oh0';

const subjectOptions = [
  'General Inquiry',
  'Cloud Solutions',
  'Cybersecurity Services',
  'AI & Analytics',
  'IT Consulting',
  'Partnership Opportunity',
  'Other',
];

// Arrow SVG used for the custom select background
const SELECT_ARROW_BG =
  "url('data:image/svg+xml,%3Csvg xmlns=\"http://www.w3.org/2000/svg\" width=\"12\" height=\"12\" viewBox=\"0 0 12 12\"%3E%3Cpath fill=\"%236B7280\" d=\"M6 8L1 3h10z\"/%3E%3C/svg%3E')";

export default function InquiryForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      // Guard against overly long messages (should never happen thanks to maxLength attr)
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const urlEncoded = new URLSearchParams();
      formData.forEach((value, key) => {
        // FormData values can be File, string, etc. We only expect strings here.
        urlEncoded.append(key, String(value));
      });

      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });

      if (res.ok) {
        setSubmitStatus('success');
        form.reset();
        setMessageLength(0);
      } else {
        setSubmitStatus('error');
      }
    } catch (err) {
      console.error('Form submission failed:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-6 sm:p-8">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-1">Send Us a Message</h2>
        <p className="text-sm text-gray-500">
          Fill out the form and our team will get back to you within 24 hours.
        </p>
      </div>

      {submitStatus === 'success' && (
        <div className="mb-6 p-4 rounded-lg bg-green-50 border border-green-200 flex items-start gap-3">
          <div className="w-5 h-5 flex items-center justify-center mt-0.5">
            <i className="ri-checkbox-circle-fill text-green-600 text-lg"></i>
          </div>
          <div>
            <p className="text-sm font-semibold text-green-800">Message sent successfully!</p>
            <p className="text-xs text-green-600 mt-0.5">We&apos;ll be in touch shortly.</p>
          </div>
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200 flex items-start gap-3">
          <div className="w-5 h-5 flex items-center justify-center mt-0.5">
            <i className="ri-error-warning-fill text-red-600 text-lg"></i>
          </div>
          <div>
            <p className="text-sm font-semibold text-red-800">Something went wrong.</p>
            <p className="text-xs text-red-600 mt-0.5">Please try again or email us directly.</p>
          </div>
        </div>
      )}

      <form
        id="contact-inquiry-form"
        data-readdy-form
        onSubmit={handleSubmit}
        className="space-y-5"
      >
        {/* Name & Email Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label
              htmlFor="fullName"
              className="block text-xs font-semibold text-gray-700 mb-1.5"
            >
              Full Name *
            </label>
            <input
              id="fullName"
              name="fullName"
              type="text"
              required
              placeholder="John Doe"
              className="w-full h-11 px-4 text-sm border border-gray-200 rounded-lg focus:border-[#0066CC] focus:ring-1 focus:ring-[#0066CC]/20 outline-none transition-all placeholder-gray-400"
            />
          </div>
          <div>
            <label
              htmlFor="email"
              className="block text-xs font-semibold text-gray-700 mb-1.5"
            >
              Email Address *
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              placeholder="john@company.com"
              className="w-full h-11 px-4 text-sm border border-gray-200 rounded-lg focus:border-[#0066CC] focus:ring-1 focus:ring-[#0066CC]/20 outline-none transition-all placeholder-gray-400"
            />
          </div>
        </div>

        {/* Company & Phone Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label
              htmlFor="company"
              className="block text-xs font-semibold text-gray-700 mb-1.5"
            >
              Company
            </label>
            <input
              id="company"
              name="company"
              type="text"
              placeholder="Your Company"
              className="w-full h-11 px-4 text-sm border border-gray-200 rounded-lg focus:border-[#0066CC] focus:ring-1 focus:ring-[#0066CC]/20 outline-none transition-all placeholder-gray-400"
            />
          </div>
          <div>
            <label
              htmlFor="phone"
              className="block text-xs font-semibold text-gray-700 mb-1.5"
            >
              Phone Number
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              placeholder="+1 (555) 000-0000"
              className="w-full h-11 px-4 text-sm border border-gray-200 rounded-lg focus:border-[#0066CC] focus:ring-1 focus:ring-[#0066CC]/20 outline-none transition-all placeholder-gray-400"
            />
          </div>
        </div>

        {/* Subject */}
        <div>
          <label
            htmlFor="subject"
            className="block text-xs font-semibold text-gray-700 mb-1.5"
          >
            Subject *
          </label>
          <select
            id="subject"
            name="subject"
            required
            defaultValue=""
            className="w-full h-11 px-4 text-sm border border-gray-200 rounded-lg focus:border-[#0066CC] focus:ring-1 focus:ring-[#0066CC]/20 outline-none transition-all text-gray-700 bg-white cursor-pointer appearance-none"
            style={{
              backgroundImage: SELECT_ARROW_BG,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 16px center',
            }}
          >
            <option value="" disabled>
              Select a subject
            </option>
            {subjectOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        {/* Message */}
        <div>
          <label
            htmlFor="message"
            className="block text-xs font-semibold text-gray-700 mb-1.5"
          >
            Message *{' '}
            <span className="font-normal text-gray-400">
              ({messageLength}/500)
            </span>
          </label>
          <textarea
            id="message"
            name="message"
            required
            maxLength={500}
            rows={5}
            placeholder="Tell us about your project or inquiry..."
            onChange={(e) => setMessageLength(e.target.value.length)}
            className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:border-[#0066CC] focus:ring-1 focus:ring-[#0066CC]/20 outline-none transition-all placeholder-gray-400 resize-vertical"
          ></textarea>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full h-12 bg-[#0066CC] text-white rounded-lg font-semibold text-sm hover:bg-[#0055AA] transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
        >
          {isSubmitting ? (
            <>
              <i className="ri-loader-4-line animate-spin text-lg"></i>
              Sending...
            </>
          ) : (
            <>
              Send Message
              <i className="ri-send-plane-fill text-base"></i>
            </>
          )}
        </button>
      </form>
    </div>
  );
}
